import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, BookOpen, Calculator, Mail, Phone, MapPin, ChevronRight, Star, Users, Award, Globe, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = '';

export default function LandingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [showContactSuccess, setShowContactSuccess] = useState(false);
  const [tutorProfiles, setTutorProfiles] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tutor-profiles`).then(res => setTutorProfiles(res.data)).catch(() => {});
  }, []);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setShowContactSuccess(true);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-6">
              <Link to="/about" className="text-sm font-semibold hover:text-[#002FA7]">About Us</Link>
              <a href="#services" className="text-sm font-semibold hover:text-[#002FA7]">Services</a>
              <Link to="/tutors" className="text-sm font-semibold hover:text-[#002FA7]">Our Tutors</Link>
              <a href="#contact" className="text-sm font-semibold hover:text-[#002FA7]">Contact</a>
              {user ? (
                <>
                  <button
                    onClick={() => navigate(user.role === 'admin' ? '/admin' : user.role === 'tutor' ? '/tutor' : '/student')}
                    className="text-sm font-semibold hover:text-[#002FA7]"
                    data-testid="dashboard-nav-link"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={logout}
                    className="text-sm font-semibold px-4 py-2 rounded-md border border-slate-900 hover:bg-slate-900 hover:text-white"
                    data-testid="logout-button"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-semibold hover:text-[#002FA7]" data-testid="login-nav-link">
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-[#002FA7] text-white hover:bg-[#001a5c]"
                    data-testid="register-nav-link"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/1e543127-7b1e-4053-8cdb-6c44d7e5faf5/images/06f03ac1b1d4cb2fd02d6df5fff2a1a87caf95c2fcd2936eb8f3bafaf389c669.png"
            alt="Professional tutoring study space for French, English, and Math lessons"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/40"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.1] mb-6" data-testid="hero-heading">
              Elite French-English & Math Tutoring
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8">
              World-class instruction in French, English, and Mathematics. Our experienced tutors provide personalized learning experiences tailored to your goals. Available both online and in-person.
            </p>
            <div className="flex gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
                data-testid="hero-cta-button"
              >
                Book a Session <ChevronRight className="w-5 h-5" />
              </Link>
              <a
                href="#services"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-900 text-slate-900 font-bold rounded-md hover:bg-slate-900 hover:text-white"
              >
                Learn More
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-black text-[#002FA7]">500+</div>
                <div className="text-sm text-slate-600 font-semibold">Students</div>
              </div>
              <div>
                <div className="text-3xl font-black text-[#002FA7]">15+</div>
                <div className="text-sm text-slate-600 font-semibold">Expert Tutors</div>
              </div>
              <div>
                <div className="text-3xl font-black text-[#002FA7]">98%</div>
                <div className="text-sm text-slate-600 font-semibold">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">Our Services</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Professional tutoring services for students and adults
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-[#F3F4F6] p-8 border border-slate-200 hover:-translate-y-1 hover:shadow-lg" data-testid="service-french">
              <div className="w-16 h-16 bg-[#002FA7] rounded-md flex items-center justify-center mb-6">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">French Language</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Master French with native speakers. K-12 students and FSL (French as a Second Language) for adults. From beginner to advanced levels.
              </p>
            </div>
            <div className="bg-[#F3F4F6] p-8 border border-slate-200 hover:-translate-y-1 hover:shadow-lg" data-testid="service-english">
              <div className="w-16 h-16 bg-[#E32636] rounded-md flex items-center justify-center mb-6">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">English ESL</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                K-12 English and ESL (English as a Second Language) for adults. TOEFL, IELTS prep, and business English available.
              </p>
            </div>
            <div className="bg-[#F3F4F6] p-8 border border-slate-200 hover:-translate-y-1 hover:shadow-lg" data-testid="service-creole">
              <div className="w-16 h-16 bg-[#22c55e] rounded-md flex items-center justify-center mb-6">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Haitian Creole</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Learn Haitian Creole with a native speaker. All levels welcome — from beginners to advanced conversation and cultural fluency.
              </p>
            </div>
            <div className="bg-[#F3F4F6] p-8 border border-slate-200 hover:-translate-y-1 hover:shadow-lg" data-testid="service-math">
              <div className="w-16 h-16 bg-[#FFC000] rounded-md flex items-center justify-center mb-6">
                <Calculator className="w-8 h-8 text-slate-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">Mathematics</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Personalized math tutoring for grades 1-12. From elementary arithmetic to advanced calculus. Build confidence and achieve top grades.
              </p>
            </div>
          </div>

          <div className="bg-[#F3F4F6] p-8 border border-slate-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2">Flexible Pricing</h3>
              <p className="text-slate-600">Choose the session length that works for you</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="bg-white p-6 border border-slate-200 text-center">
                <div className="text-3xl font-black text-[#002FA7] mb-2">$40</div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600">30 Minutes</div>
              </div>
              <div className="bg-white p-6 border border-slate-200 text-center">
                <div className="text-3xl font-black text-[#002FA7] mb-2">$55</div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600">45 Minutes</div>
              </div>
              <div className="bg-white p-6 border border-slate-200 text-center">
                <div className="text-3xl font-black text-[#002FA7] mb-2">$60</div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600">60 Minutes</div>
              </div>
              <div className="bg-white p-6 border border-slate-200 text-center">
                <div className="text-3xl font-black text-[#002FA7] mb-2">$90</div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600">90 Minutes</div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <div className="inline-block bg-white px-6 py-3 border border-slate-200 rounded-md">
                <div className="text-sm font-bold mb-1">Special Offering</div>
                <div className="text-slate-600 text-sm">Haitian Creole as a Second Language also available - Native speaker instructor</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="tutors" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">Meet Our Expert Tutors</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Qualified professionals with years of teaching experience
            </p>
          </div>
          {tutorProfiles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {tutorProfiles.slice(0, 3).map((profile) => (
                  <div key={profile.profile_id} className="bg-white p-6 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    <div className="h-64 bg-slate-100 overflow-hidden mb-4">
                      {profile.photo_url ? (
                        <img src={profile.photo_url} alt={`${profile.name} - Professional tutor`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#002FA7]/10 to-[#002FA7]/5">
                          <span className="text-6xl font-black text-[#002FA7]/20">{profile.name?.charAt(0) || 'T'}</span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{profile.name}</h3>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {profile.subjects?.map((s, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold rounded">
                          <BookOpen className="w-3 h-3" /> {s}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                      <Clock className="w-4 h-4" /> {profile.years_experience}+ years experience
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{profile.bio}</p>
                  </div>
                ))}
              </div>
              {tutorProfiles.length > 3 && (
                <div className="text-center mt-8">
                  <Link to="/tutors" className="inline-flex items-center gap-2 text-[#002FA7] font-bold hover:underline">
                    View All Tutors <ChevronRight className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white p-6 border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1600896997793-b8ed3459a17f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjB0dXRvciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTY3MjM2MHww&ixlib=rb-4.1.0&q=85"
                  alt="Sophie Martin - Professional French and English tutor with 10+ years experience"
                  className="w-full h-64 object-cover mb-4"
                />
                <h3 className="text-xl font-bold mb-2">Sophie Martin</h3>
                <p className="text-sm text-slate-600 mb-3">French & English Specialist</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  10+ years experience teaching French and English. Native French speaker with TESOL certification.
                </p>
              </div>
              <div className="bg-white p-6 border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1593052393678-d1c690c76071?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB0dXRvciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTY3MjM2MHww&ixlib=rb-4.1.0&q=85"
                  alt="Dr. James Chen - PhD Mathematics expert specializing in algebra and calculus"
                  className="w-full h-64 object-cover mb-4"
                />
                <h3 className="text-xl font-bold mb-2">Dr. James Chen</h3>
                <p className="text-sm text-slate-600 mb-3">Mathematics Expert</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  PhD in Mathematics. Specializes in algebra, calculus, and standardized test prep.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">Why Choose Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#002FA7] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Personalized Approach</h3>
              <p className="text-slate-600">One-on-one sessions tailored to your learning style and pace</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E32636] rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Proven Results</h3>
              <p className="text-slate-600">98% of our students see grade improvements within 3 months</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FFC000] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-slate-900" />
              </div>
              <h3 className="text-xl font-bold mb-2">Certified Tutors</h3>
              <p className="text-slate-600">All tutors hold advanced degrees and teaching certifications</p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">Contact Us</h2>
            <p className="text-base text-slate-600">Get in touch to schedule your first session</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 border border-slate-200">
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    required
                    data-testid="contact-message-input"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
                  data-testid="contact-submit-button"
                >
                  Send Message
                </button>
              </form>
            </div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 text-[#002FA7] mt-1" />
                <div>
                  <div className="font-bold mb-1">Email</div>
                  <div className="text-slate-600">eslelitetutoring@gmail.com</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 text-[#002FA7] mt-1" />
                <div>
                  <div className="font-bold mb-1">Phone</div>
                  <div className="text-slate-600">+1 (226) 920-3856</div>
                  <div className="text-slate-600">+1 (437) 677-6164</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-[#002FA7] mt-1" />
                <div>
                  <div className="font-bold mb-1">Location</div>
                  <div className="text-slate-600">Available worldwide via online sessions</div>
                </div>
              </div>
              <div className="mt-6 rounded-md overflow-hidden border border-slate-200">
                <iframe
                  title="Elite French-English & Math Tutoring - Available Worldwide"
                  src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d40000000!2d-40!3d30!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f2!5e0!3m2!1sen!2sca!4v1700000000000!5m2!1sen!2sca"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="world-map"
                ></iframe>
                <div className="bg-[#002FA7] text-white text-center py-2 text-sm font-semibold">
                  Serving Students Worldwide - Online & In-Person
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showContactSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowContactSuccess(false)}>
          <div className="bg-white p-8 border-2 border-[#002FA7] rounded-md max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-black mb-3" data-testid="contact-success-message">Thank You!</h3>
              <p className="text-slate-600 mb-2">
                Thank you for contacting <strong>Elite French-English & Math Tutoring</strong>.
              </p>
              <p className="text-slate-600 mb-6">
                We will get back to you within 24 hours.
              </p>
              <button
                onClick={() => setShowContactSuccess(false)}
                className="w-full py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
                data-testid="contact-success-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="text-sm text-slate-400">
              © 2026 Elite French-English & Math Tutoring. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
